import type { Gate, Tower } from '../../../data/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { boxSolid, pyramidOverRing, subFrame } from '../solids';
import { gableRoof } from '../roofSolids';
import { archedBlock } from '../landmarks/fortress/fortressBlocks';
import { buildTowerMasonry } from './towerMasonry';

/** Compound gate seen in the 1538 painting (museum scan 6000–6430 × 2680–3030).
 * Main gate and larger outer gate are distinct buildings linked by low court walls. Positions
 * remain on the source gate's existing footprint; interior proportions and elevations are estimates.
 */
interface Styles {
  readonly wall: VertexStyle;
  readonly roof: VertexStyle;
  readonly slate: VertexStyle;
  readonly dark: VertexStyle;
}

function gatehouse(
  builder: MeshBuilder,
  styles: Styles,
  frame: Frame,
  u: readonly [number, number],
  halfWidth: number,
  eave: number,
  rise: number,
  passage: number
): void {
  const v = [-halfWidth, halfWidth] as const;
  archedBlock(builder, styles.wall, frame, {
    u,
    v,
    foot: -0.5,
    top: eave,
    passage: { v: [-passage, passage], springing: 2.5 },
    openTop: true,
  });
  gableRoof(builder, styles, frame, {
    u0: u[0],
    u1: u[1],
    v0: v[0],
    v1: v[1],
    eaveY: eave,
    ridgeY: eave + rise,
    ends: { start: true, end: true },
  });
}

function tower(
  builder: MeshBuilder,
  styles: Styles,
  frame: Frame,
  at: readonly [number, number],
  radius: number,
  height: number,
  rise: number,
  square: boolean
): void {
  const local: Tower = {
    id: 'haarlemmer-gate-component',
    pos: [0, 0],
    radius,
    height,
    shape: square ? 'square' : 'round',
    roof: 'cone',
    sourceRefs: ['painting-1538'],
  };
  const count = square ? 4 : 16;
  const ring = Array.from({ length: count }, (_, i): readonly [number, number] => {
    const angle = (i / count) * Math.PI * 2 + (square ? Math.PI / 4 : 0);
    const r = square ? radius * Math.SQRT2 : radius;
    return [Math.cos(angle) * r, Math.sin(angle) * r];
  });
  const localFrame = subFrame(frame, at[0], at[1], 0);
  buildTowerMasonry(builder, styles.wall, localFrame, local, ring, height, false, styles.dark);
  pyramidOverRing(builder, { wall: styles.wall, roof: styles.slate }, localFrame, ring, height, {
    plan: [0, 0],
    y: height + rise,
  });
}

export function buildHaarlemmerpoort(
  builder: MeshBuilder,
  styles: Styles,
  frame: Frame,
  gate: Gate
): void {
  const halfDepth = gate.depth / 2;
  const halfWidth = gate.width / 2;
  const passage = Math.min(2, gate.width * 0.2);
  const flank = halfWidth * 0.7;
  const bodyHalf = halfWidth * 0.74;
  const radius = halfWidth * 0.3;
  const mainEnd = -halfDepth * 0.43;
  const outerStart = halfDepth * 0.43;
  gatehouse(builder, styles, frame, [-halfDepth, mainEnd], bodyHalf, gate.height, 1.7, passage);
  gatehouse(
    builder,
    styles,
    frame,
    [outerStart, halfDepth],
    bodyHalf,
    gate.height * 0.83,
    1.6,
    passage
  );
  for (const side of [-1, 1]) {
    tower(
      builder,
      styles,
      frame,
      [-halfDepth * 0.71, side * flank],
      radius,
      gate.height * 1.08,
      3.0,
      false
    );
    tower(
      builder,
      styles,
      frame,
      [halfDepth * 0.78, side * flank],
      radius * 0.86,
      gate.height * (side < 0 ? 1.3 : 1.42),
      side < 0 ? 4 : 4.4,
      true
    );
    const v = side * (bodyHalf - 0.2);
    boxSolid(builder, styles.wall, frame, [mainEnd, -0.5, v - 0.2], [outerStart, 4.5, v + 0.2], {
      top: true,
      bottom: false,
    });
    boxSolid(
      builder,
      styles.wall,
      frame,
      [mainEnd + 0.03, 4.5, v - 0.25],
      [outerStart - 0.03, 4.7, v + 0.25]
    );
  }
}
